import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ArrowLeft,
  Briefcase,
  FileText,
  Calendar,
  Users,
  AlertCircle
} from 'lucide-react'
import api from '../config/api'
import { formatDate } from '../utils/formatDate'
import '../styles/MatterDetails.css'

function MatterDetails() {
  const { matterId } = useParams()
  const [matter, setMatter] = useState(null)
  const [documents, setDocuments] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchMatterData()
  }, [matterId])

  const fetchMatterData = async () => {
    try {
      setLoading(true)
      const [matterRes, docsRes] = await Promise.all([
        api.get(`/matters/${matterId}`),
        api.get('/documents', { params: { matterId } })
      ])
      setMatter(matterRes.data.data)
      setDocuments(docsRes.data.data)
    } catch (err) {
      setError('Failed to load matter details')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading matter details...</p>
      </div>
    )
  }

  if (error || !matter) {
    return (
      <div className="error-container">
        <AlertCircle size={48} />
        <h2>Error Loading Matter</h2>
        <p>{error || 'Matter not found'}</p>
        <Link to="/matters" className="btn btn-primary">
          Back to Matters
        </Link>
      </div>
    )
  }

  const statusColors = {
    open: 'status-open',
    active: 'status-active',
    pending: 'status-pending',
    closed: 'status-closed'
  }

  return (
    <div className="matter-details-page">
      <header className="page-header">
        <Link to="/matters" className="back-link">
          <ArrowLeft size={20} />
          Back to Matters
        </Link>
      </header>

      <motion.div
        className="matter-card-large"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="matter-card-header">
          <div className="matter-icon">
            <Briefcase size={24} />
          </div>
          <div className="matter-title-section">
            <span className="matter-number">{matter.matterNumber}</span>
            <h1>{matter.title}</h1>
            <span className={`matter-status ${statusColors[matter.status] || ''}`}>
              {matter.status}
            </span>
          </div>
        </div>

        {matter.description && (
          <div className="matter-description">
            <h3>Description</h3>
            <p>{matter.description}</p>
          </div>
        )}

        <div className="matter-info-grid">
          <div className="info-item">
            <span className="info-label">Type</span>
            <span className="info-value">{matter.type}</span>
          </div>
          {matter.practiceArea && (
            <div className="info-item">
              <span className="info-label">Practice Area</span>
              <span className="info-value">{matter.practiceArea}</span>
            </div>
          )}
          <div className="info-item">
            <span className="info-label">Opened</span>
            <span className="info-value">
              {matter.openDate ? formatDate(matter.openDate) : 'N/A'}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Last Updated</span>
            <span className="info-value">
              {matter.updatedAt ? formatDate(matter.updatedAt) : 'N/A'}
            </span>
          </div>
        </div>
      </motion.div>

      {/* Documents Section */}
      <section className="matter-section">
        <div className="section-header">
          <FileText size={20} />
          <h2>Documents</h2>
        </div>
        {documents.length === 0 ? (
          <div className="empty-section">
            <p>No documents available for this matter</p>
          </div>
        ) : (
          <div className="documents-list">
            {documents.map((doc) => (
              <div key={doc.id} className="document-item">
                <FileText size={18} />
                <div className="document-info">
                  <span className="document-name">{doc.name}</span>
                  <span className="document-meta">
                    {doc.type} - {formatFileSize(doc.size)}
                  </span>
                </div>
                <span className="document-date">
                  {formatDate(doc.createdAt)}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

function formatFileSize(bytes) {
  if (!bytes) return ''
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

export default MatterDetails
